import React from "react";
import { get } from "lodash";
import PropTypes from "prop-types";
import { Link, SocialShare } from "@quintype/components";
import ResponsiveImageWithFallback from "../../atoms/responsive-image-with-fallback";
import styles from "./styles.m.css";
import { generateImageSources } from "../../utils/utils";
import Carousel from "../../atoms/carousel";
import { SocialShareTemplate } from "./social-share/index";
import { GA, WebEngage } from "../../../integrations";
import { PAGE_TYPE } from "../../../constants";
import { Icon } from "../../atoms/icon";
import assetify from "@quintype/framework/assetify";
import homeIcon from "../../../../assets/images/vikatan-homeicon.png";
import { humanizeDate } from "../../utils/time-format";

const ShortNewsCardMobile = ({ story, cardWithImageZoom = true, className = "", hasTruncatedHeadline = true }) => {
  const contributor = get(story, ["author-name"], null);
  const headline = get(story, ["headline"], null);
  const subheadline = get(story, ["subheadline"], null);
  const imageURL = get(story, ["hero-image-s3-key"], null);
  const imageMetaData = get(story, ["hero-image-metadata"], null);
  const SourceLink = get(story, ["url"], null);
  const publishedAt = get(story, ["published-at"], null);

  if (!(headline && SourceLink)) {
    return null;
  }

  return (
    <div className={styles["short-news-wrapper-base"]}>
      <div className={styles["short-news-wrapper"]}>
        <div className={styles["image-author"]}>
          <ResponsiveImageWithFallback
            className={styles["image-wrapper"]}
            slug={imageURL}
            metadata={imageMetaData}
            imgParams={{ auto: ["format", "compress"] }}
            sources={generateImageSources(
              { aspectRatio: [9, 6], screenWidthCoverage: 0.5 },
              { aspectRatio: [9, 6], screenWidthCoverage: 0.25 }
            )}
            zoom={false}
            story={story}
          />
          <div className={styles["author-google-follow"]}>
            <Icon type="author" className={styles["icon"]} />
            <span className={styles["contributor-name"]}>{contributor}</span>
          </div>
        </div>

        <div className={styles["text-wrapper"]}>
          <div className={styles["top-grid"]}>
            <span className={styles["published"]}>
              <time className={styles.time} dateTime={new Date(publishedAt).toISOString()}>
                {humanizeDate(publishedAt)}
              </time>
            </span>
            <SocialShare
              className={styles["share"]}
              template={SocialShareTemplate}
              fullUrl={SourceLink}
              title={headline}
              story={story}
            />
          </div>
          <h1 className={`${styles["headline"]} ${hasTruncatedHeadline ? styles["truncated"] : ""} `}>{headline}</h1>

          <h3 className={styles["sub-headline"]}>
            {subheadline}
            <span className={styles["sub-readmore"]}>
              <Link
                aria-label={`${"Read full story: "} ${headline}`}
                className={`${styles["read-more-link"]} ${className} ${
                  cardWithImageZoom ? "card-with-image-zoom" : ""
                }`}
                href={`${SourceLink}?pfrom=short-news`}
                externalLink={`${SourceLink}?pfrom=short-news`}
              >
                <h3
                  className={styles["read-more"]}
                  onClick={() => {
                    if ("ga" in window) {
                      window.ga("send", "event", "short-news", "click", SourceLink);
                    }
                    if ("gtag" in window) {
                      window.gtag("event", "short-news", {
                        event_category: "click",
                        event_label: SourceLink
                      });
                    }
                  }}
                >
                  <span>மேலும் படிக்க </span>
                  <div className={styles["arrows"]}>
                    <Icon type={"angle-right"} />
                    <Icon type={"angle-right"} />
                  </div>
                </h3>
              </Link>
            </span>
          </h3>
        </div>
      </div>
    </div>
  );
};

ShortNewsCardMobile.propTypes = {
  cardWithImageZoom: PropTypes.bool,
  className: PropTypes.string,
  hasTruncatedHeadline: PropTypes.bool,
  story: PropTypes.shape({
    "author-name": PropTypes.string,
    headline: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    "hero-image-metadata": PropTypes.string,
    "hero-image-s3-key": PropTypes.string,
    "published-at": PropTypes.number,
    subheadline: PropTypes.string
  })
};

const ShortNewsPage = data => {
  const stories = get(data, ["data", "latest", "collection", "items"], null);
  let [current, setCurrent] = React.useState(0);

  React.useEffect(
    () => {
      GA.registerPageView(PAGE_TYPE.SHORT_NEWS, {});
      WebEngage.registerPageView();
    },
    [current]
  );

  const slides = stories.map(story => (
    <ShortNewsCardMobile key={story.id} story={story.story} className={styles.card} />
  ));

  function handleSlideChangeMobile(index) {
    setCurrent(index);
  }

  return (
    <div className={styles["shortnews-container"]}>
      <div className={styles["header"]}>
        <div className={styles["title-div"]}>
          <div className={styles["title-wrapper"]}>
            <img src="https://media.vikatan.com/vikatan/2021-10/3552b2f7-c1f9-46f9-b98b-c175fed866dc/vikatan_bytes.png" />
          </div>
        </div>
        <div className={styles["close-div"]}>
          <a href="https://www.vikatan.com?p_from=short-news" className={styles["home-btn"]}>
            <img src={assetify(homeIcon)} alt="" />
          </a>
        </div>
      </div>
      <Carousel
        className={styles["carousel"]}
        afterSlide={handleSlideChangeMobile}
        options={{
          type: "carousel",
          perView: 1,
          gap: 0,
          rewind: false,

          breakpoints: {
            767: {
              perView: 1,
              gap: 0
            },
            991: {
              perView: 1,
              gap: 0
            }
          }
        }}
        renderCenterLeftControls={({ previousSlide, currentSlide }) =>
          currentSlide > 0 ? (
            <button
              aria-label="previous slide"
              role="button"
              onClick={previousSlide}
              className={`${styles["slider-nav-button"]}`}
            >
              <Icon type="angle-left" className={styles.icon} />
            </button>
          ) : null
        }
        renderCenterRightControls={({ nextSlide }) => (
          <button
            aria-label="next slide"
            role="button"
            onClick={nextSlide}
            className={`${styles["slider-nav-button"]}`}
          >
            <Icon type="angle-right" className={styles.icon} />
          </button>
        )}
        childClass={styles["glide_li"]}
      >
        {slides}
      </Carousel>
    </div>
  );
};

export { ShortNewsPage };
